import React, { useEffect, useState } from "react";
import { Topbar, Alert } from "@components";
import { useLocation, useNavigate } from "react-router-dom";
import {
  InfoBox,
  RootContainer,
  ButtonWrapper,
  SpinWrapper,
  ItemBox,
  Image,
  Text,
  EmptyIcon,
  EmptyBox,
  NumberText,
  EmptyText,
  Label,
} from "./WithdrawAccounts.style";
import { Button, Radio, Spin } from "antd";
import TermsConditions from "../TermsConditions/TermsConditions";
import { PRIVATE_ROUTE, PUBLIC_ROUTE } from "@router/route.constants";
import { WITHDRAW_LK, UPDATE_MODE_LK } from "@constants";
import { api } from "@api";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ENGLISH_LANGUAGE_CODE_LK, BURMESE_LANGUAGE_CODE_LK } from "@constants";
import { localStorage } from "@utils";
import { PlusCircleOutlined } from "@ant-design/icons";
import { EditOutlined } from "@assets";
import { setHasWithdrawAccount, openAlert } from "../../../redux/app/appSlice";

const WithdrawAccounts = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  // handle existing multiple old accounts
  const [isMultiple, setIsMultiple] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectLoading, setSelectLoading] = useState(false);

  const userPreferedLanguage = localStorage.getUserPreferedLanguage();
  const { withdraw_rule } = useSelector((state) => state.app.settings);

  const withdrawRule =
    userPreferedLanguage === ENGLISH_LANGUAGE_CODE_LK
      ? withdraw_rule?.text_en
      : userPreferedLanguage === BURMESE_LANGUAGE_CODE_LK
        ? withdraw_rule?.text_mm
        : withdraw_rule?.text_ch;

  useEffect(() => {
    getUserPayments();
    getUserPaymentCategories();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setIsMultiple(data.length > 1);

    // eslint-disable-next-line
  }, [data]);

  const getUserPayments = () => {
    setLoading(true);
    api
      .getUserPayments()
      .then(({ user_payments }) => {
        setLoading(false);
        setData(user_payments);
        dispatch(setHasWithdrawAccount(user_payments.length > 0));
      })
      .catch(() => { });
  };

  const getUserPaymentCategories = () => {
    api
      .getUserPaymentCategories()
      .then(({ payment_categories }) => {
        setHasMore(payment_categories.length > 0);
      })
      .catch(() => { });
  };

  const handleSelectClicked = () => {
    if (!selectLoading) {
      setSelectLoading(true);
      api
        .chooseUserPayment({
          user_payment_id: selectedItem.user_payment_id
        })
        .then(() => {
          setSelectLoading(false);
          showAlert({
            message: t("wallet.selectAccSuccessMsg"),
            successCallback: () => {
              setData([selectedItem]);
              setSelectedItem(null);
            }
          });
        })
        .catch((errorMsg) => {
          setSelectLoading(false);
          showAlert({
            message: errorMsg,
            isError: true,
          });
        });
    }
  }

  const showAlert = (data) => {
    dispatch(openAlert(data));
  };

  const goBack = () => {
    if (state?.isSpin) {
      navigate(`/${PUBLIC_ROUTE.SPIN_HOME}`);
      return;
    }
    if (state?.isNoti) {
      navigate(`/${PUBLIC_ROUTE.HOME}/${PUBLIC_ROUTE.NOTIFICATION}`);
      return;
    }
    navigate(`/${PUBLIC_ROUTE.HOME}/${PRIVATE_ROUTE.WALLET}`);
  };

  const handleNewClicked = () => {
    navigate(`/${PRIVATE_ROUTE.ACCOUNT_TYPE}/${WITHDRAW_LK}`);
  };

  const handleItemClicked = (item) => {
    if (isMultiple) {
      setSelectedItem(item);
      return;
    }
    navigate(`/${PRIVATE_ROUTE.CONFIRM_PIN}`, { state: item });
  };

  const handleEditClicked = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/${PRIVATE_ROUTE.CREATE_UPDATE_ACCOUNT}/${UPDATE_MODE_LK}`, {
      state: { item },
    });
  };

  const Item = ({ item }) => (
    <ItemBox onClick={() => handleItemClicked(item)}>
      <Image
        src={item.payment_category_image}
        radius={item.payment_category_name === "KBZ" ? "none" : "default"}
      />
      <InfoBox>
        <Text>{item.name}</Text>
        <NumberText>{item.num}</NumberText>
      </InfoBox>
      {isMultiple && <Radio onClick={() => handleItemClicked(item)} checked={selectedItem?.user_payment_id === item.user_payment_id} />}
      {!isMultiple && <EditOutlined onClick={(e) => handleEditClicked(e, item)} />}
    </ItemBox>
  );

  const Empty = () => (
    <EmptyBox>
      <EmptyIcon />
      <EmptyText>{t("wallet.withdrawAccountNoList")}</EmptyText>
    </EmptyBox>
  );

  const Loader = () => (
    <SpinWrapper>
      <Spin />
    </SpinWrapper>
  );

  return (
    <>
      <Topbar title={t("wallet.walletWithdraw")} goBack={goBack} />
      {loading && <Loader />}
      {!loading && (
        <RootContainer>
          {data.length === 0 && <Empty />}
          {isMultiple && <Label style={{ paddingBottom: '16px' }}>*{t("wallet.multipleAccInfoLabel")}*</Label>}
          {data.map((item, key) => (
            <Item key={key} item={item} />
          ))}
          {(data.length === 0 || isMultiple) &&
            <ButtonWrapper fullWidth={data.length > 0 ? "true" : "false"}>
              {data.length === 0 &&
                <Button
                  type="primary"
                  size="large"
                  onClick={handleNewClicked}
                  disabled={!hasMore}
                  icon={
                    <PlusCircleOutlined
                      style={{
                        color: "#fff",
                      }}
                    />
                  }
                >
                  {t("wallet.withdrawAccountAdd")}
                </Button>}

              {isMultiple &&
                <Button
                  type="primary"
                  size="large"
                  onClick={handleSelectClicked}
                  disabled={!selectedItem}
                  loading={selectLoading}
                >
                  {t("wallet.selectWithdrawAccBtnLabel")}
                </Button>
              }
            </ButtonWrapper>}
          <TermsConditions
            title={t("wallet.walletWithdraw")}
            rule={withdrawRule}
          />
        </RootContainer>
      )}
      <Alert />
    </>
  );
};

export default WithdrawAccounts;
